import React from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import { AnimatedSection, AnimatedTableRow } from '../../components/animations/MotionContainers'
import { MetricCard } from '../../components/dashboard/MetricCard'
import { API_REQUESTS_PAUSED } from '../../data'
import { getAuditEvents } from '../../services/admin/auditApi'
import { formatDateTime, getApiErrorMessage, getPaginatedItems } from '../../utils/apiData'

const severityTone = {
  INFO: 'success',
  WARNING: 'warning',
  ERROR: 'error',
  CRITICAL: 'error',
  SUCCESS: 'success',
  LOGOUT: 'success',
  LOGIN: 'success',
}

const severityIcon = {
  INFO: <CheckCircleOutlineRoundedIcon />,
  WARNING: <WarningAmberRoundedIcon />,
  ERROR: <ErrorOutlineRoundedIcon />,
  CRITICAL: <ErrorOutlineRoundedIcon />,
  SUCCESS: <CheckCircleOutlineRoundedIcon />,
}

const emptyDateFilters = {
  createdFrom: '',
  createdTo: '',
}

const toIsoDateFilter = (value, endOfDay = false) => {
  if (!value) return undefined

  const date = new Date(`${value}T${endOfDay ? '23:59:59.999' : '00:00:00.000'}`)
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString()
}

const buildAuditQueryParams = (filters, pagination = {}) => ({
  ...pagination,
  ...(filters.createdFrom ? { createdFrom: toIsoDateFilter(filters.createdFrom) } : {}),
  ...(filters.createdTo ? { createdTo: toIsoDateFilter(filters.createdTo, true) } : {}),
})

const DateFilterField = ({ label, name, value, onChange }) => (
  <Box className="admin-date-field">
    <Typography component="label" htmlFor={`audit-${name}`}>{label}</Typography>
    <TextField
      id={`audit-${name}`}
      name={name}
      type="date"
      size="small"
      value={value}
      onChange={onChange}
      inputProps={{ 'aria-label': label }}
      fullWidth
    />
  </Box>
)

const getAuditSeverity = (event) => {
  const explicitSeverity = event.severity || event.level
  if (explicitSeverity) return String(explicitSeverity).toUpperCase()
  if (String(event.action || '').includes('ERROR')) return 'ERROR'
  return 'INFO'
}

const getAuditUserName = (event) => (
  event.users?.displayName
  || event.users?.display_name
  || event.users?.username
  || event.user?.displayName
  || event.user?.username
  || event.user_id
  || 'Sistema'
)

const normalizeAuditEvent = (event) => {
  const severity = getAuditSeverity(event)

  return {
    id: event.id || event.event_uuid || `${event.action}-${event.created_at}`,
    event: event.action || 'Evento',
    user: getAuditUserName(event),
    application: event.applications?.name || event.application?.name || event.applicationCode || event.application_id || '-',
    area: event.module || '-',
    entity: [event.entity_type, event.entity_id].filter(Boolean).join(' #') || '-',
    ip: event.ip_address || '-',
    traceId: event.trace_id || event.traceId || '-',
    severity,
    createdAt: event.created_at || event.createdAt || '',
    date: formatDateTime(event.created_at || event.createdAt, 'Sin fecha'),
    icon: severityIcon[severity] || <CheckCircleOutlineRoundedIcon />,
  }
}

const buildAuditMetrics = (events, totalEvents) => {
  const eventCount = totalEvents || events.length
  const warningCount = events.filter((event) => event.severity === 'WARNING').length
  const errorCount = events.filter((event) => ['ERROR', 'CRITICAL'].includes(event.severity)).length
  const userCount = new Set(events.map((event) => event.user).filter(Boolean)).size

  return [
    { title: 'Eventos', value: String(eventCount), action: '', icon: <CheckCircleOutlineRoundedIcon /> },
    { title: 'Avisos', value: String(warningCount), action: '', icon: <WarningAmberRoundedIcon /> },
    { title: 'Errores', value: String(errorCount), action: '', icon: <ErrorOutlineRoundedIcon /> },
    { title: 'Usuarios', value: String(userCount), action: '', icon: <CheckCircleOutlineRoundedIcon /> },
  ]
}

const buildExcelRows = (events) => events.map((event) => ({
  Evento: event.event,
  Usuario: event.user,
  Aplicación: event.application,
  Área: event.area,
  Entidad: event.entity,
  IP: event.ip,
  Severidad: event.severity,
  Fecha: event.date,
  TraceId: event.traceId,
}))

const downloadAuditExcel = (events, filters) => {
  const worksheet = XLSX.utils.json_to_sheet(buildExcelRows(events))
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Auditoría')

  const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
  const filenameRange = filters.createdFrom || filters.createdTo
    ? `${filters.createdFrom || 'inicio'}_${filters.createdTo || 'fin'}`
    : 'todos'

  saveAs(
    new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' }),
    `auditoria_clasol_${filenameRange}.xlsx`,
  )
}

const ExportAuditDialog = ({
  open,
  onClose,
  onExport,
  exporting,
  errorMessage,
}) => {
  const [filters, setFilters] = React.useState(emptyDateFilters)

  React.useEffect(() => {
    if (open) setFilters(emptyDateFilters)
  }, [open])

  const handleChange = (event) => {
    const { name, value } = event.target
    setFilters((currentFilters) => ({ ...currentFilters, [name]: value }))
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Exportar log</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography>
            Selecciona un rango de fechas para exportar logs concretos. Si no indicas fechas, se exportarán todos los eventos disponibles.
          </Typography>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          <DateFilterField
            label="Desde"
            name="createdFrom"
            value={filters.createdFrom}
            onChange={handleChange}
          />
          <DateFilterField
            label="Hasta"
            name="createdTo"
            value={filters.createdTo}
            onChange={handleChange}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          variant="contained"
          startIcon={<DownloadRoundedIcon />}
          disabled={exporting}
          onClick={() => onExport(filters)}
        >
          {exporting ? 'Exportando...' : 'Exportar'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export const AdminAudit = () => {
  const [events, setEvents] = React.useState([])
  const [totalEvents, setTotalEvents] = React.useState(0)
  const [loading, setLoading] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState('')
  const [dateFilters, setDateFilters] = React.useState(emptyDateFilters)
  const [appliedDateFilters, setAppliedDateFilters] = React.useState(emptyDateFilters)
  const [exportDialogOpen, setExportDialogOpen] = React.useState(false)
  const [exporting, setExporting] = React.useState(false)
  const [exportErrorMessage, setExportErrorMessage] = React.useState('')

  React.useEffect(() => {
    if (API_REQUESTS_PAUSED) return undefined

    let active = true

    const loadAuditEvents = async () => {
      setLoading(true)
      setErrorMessage('')

      try {
        const response = await getAuditEvents(buildAuditQueryParams(appliedDateFilters, { page: 1, limit: 50, sortDirection: 'desc' }))
        const apiEvents = getPaginatedItems(response).map(normalizeAuditEvent)

        if (!active) return
        setEvents(apiEvents)
        setTotalEvents(response?.meta?.total || apiEvents.length)
      } catch (error) {
        if (active) setErrorMessage(getApiErrorMessage(error, 'No se han podido cargar los eventos de auditoría.'))
      } finally {
        if (active) setLoading(false)
      }
    }

    loadAuditEvents()

    return () => {
      active = false
    }
  }, [appliedDateFilters])

  const metrics = buildAuditMetrics(events, totalEvents)

  const handleDateFilterChange = (event) => {
    const { name, value } = event.target
    setDateFilters((currentFilters) => ({ ...currentFilters, [name]: value }))
  }

  const applyDateFilters = () => {
    setAppliedDateFilters(dateFilters)
  }

  const clearDateFilters = () => {
    setDateFilters(emptyDateFilters)
    setAppliedDateFilters(emptyDateFilters)
  }

  const fetchAuditEventsForExport = async (filters) => {
    if (API_REQUESTS_PAUSED) return events

    const firstResponse = await getAuditEvents(buildAuditQueryParams(filters, { page: 1, limit: 100, sortDirection: 'desc' }))
    const firstPageEvents = getPaginatedItems(firstResponse)
    const totalPages = firstResponse?.meta?.totalPages || 1

    if (totalPages <= 1) return firstPageEvents.map(normalizeAuditEvent)

    const extraPages = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, index) => (
        getAuditEvents(buildAuditQueryParams(filters, { page: index + 2, limit: 100, sortDirection: 'desc' }))
      )),
    )

    return [
      ...firstPageEvents,
      ...extraPages.flatMap(getPaginatedItems),
    ].map(normalizeAuditEvent)
  }

  const handleExport = async (filters) => {
    setExporting(true)
    setExportErrorMessage('')

    try {
      const exportEvents = await fetchAuditEventsForExport(filters)
      downloadAuditExcel(exportEvents, filters)
      setExportDialogOpen(false)
    } catch (error) {
      setExportErrorMessage(getApiErrorMessage(error, 'No se ha podido exportar el log de auditoría.'))
    } finally {
      setExporting(false)
    }
  }

  return (
    <Box className="page-fade">
      <Box className="page-heading suite-page-heading-row">
        <Box>
          <Typography variant="h4">Auditoría</Typography>
          <Typography>Consulta eventos de seguridad, cambios administrativos, accesos y errores de sistema.</Typography>
        </Box>
        <Button variant="contained" startIcon={<DownloadRoundedIcon />} onClick={() => setExportDialogOpen(true)}>Exportar log</Button>
      </Box>

      <Grid container spacing={1.5} className="audit-metrics-grid">
        {metrics.map((metric) => (
          <Grid key={metric.title} size={{ xs: 12, sm: 6, lg: 3 }}>
            <MetricCard {...metric} />
          </Grid>
        ))}
      </Grid>

      <AnimatedSection><Box className="dashboard-section-card admin-panel-card admin-page-section audit-filter-card">
        <Typography className="section-title">Filtros</Typography>
        <Box className="admin-users-filter-bar">
          <DateFilterField
            label="Desde"
            name="createdFrom"
            value={dateFilters.createdFrom}
            onChange={handleDateFilterChange}
          />
          <DateFilterField
            label="Hasta"
            name="createdTo"
            value={dateFilters.createdTo}
            onChange={handleDateFilterChange}
          />
          <Button variant="contained" size="small" onClick={applyDateFilters}>Aplicar filtros</Button>
          <Button variant="outlined" size="small" onClick={clearDateFilters}>Limpiar</Button>
        </Box>
      </Box></AnimatedSection>

      <AnimatedSection><Box className="dashboard-section-card admin-panel-card admin-page-section">
        <Typography className="section-title">Eventos recientes</Typography>
        {loading && <LinearProgress sx={{ mb: 1.5 }} />}
        {errorMessage && <Alert severity="warning" sx={{ mb: 1.5 }}>{errorMessage}</Alert>}
        <Box className="activity-table-wrap">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Evento</TableCell>
                <TableCell>Usuario</TableCell>
                <TableCell>Aplicación</TableCell>
                <TableCell>Área</TableCell>
                <TableCell>Entidad</TableCell>
                <TableCell>IP</TableCell>
                <TableCell>Severidad</TableCell>
                <TableCell>Fecha</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((event) => (
                <TableRow component={AnimatedTableRow} key={event.id} hover>
                  <TableCell>
                    <Box className="activity-app-cell">
                      <Box className="activity-row-icon">{event.icon}</Box>
                      <Box>
                        <Typography>{event.event}</Typography>
                        <Typography className="admin-table-subtext">{event.traceId}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{event.user}</TableCell>
                  <TableCell>{event.application}</TableCell>
                  <TableCell>{event.area}</TableCell>
                  <TableCell>{event.entity}</TableCell>
                  <TableCell>{event.ip}</TableCell>
                  <TableCell>
                    <Chip size="small" className={`admin-status-chip admin-status-${severityTone[event.severity] || 'success'}`} label={event.severity} />
                  </TableCell>
                  <TableCell>{event.date}</TableCell>
                </TableRow>
              ))}
              {!loading && events.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8}>
                    <Typography className="admin-user-empty">No hay eventos de auditoría registrados.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Box></AnimatedSection>

      <ExportAuditDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        onExport={handleExport}
        exporting={exporting}
        errorMessage={exportErrorMessage}
      />
    </Box>
  )
}
